import { removeHiddenChars } from "../../../utils/removeHiddenChars";

/**
 * Bulk-diff all_ratings PUT body for one student.
 * Sends only diffRatings (patched from the grid, grade omitted).
 * Do not replay snapshot contentRatings — that snapshot is from GET and
 * overwrites on-level saves that landed after load (leave-flush / slow network).
 */
export const buildDiffStudentSavePayload = ({ snapshot, ratings, comments = {}, studentKey, raterGuid }) => {
    const gradingProfileStudentId = Number(snapshot.gradingProfileStudentId);

    const diffRatings = (snapshot.diffRatings || []).map((dr) => {
        const key = `${studentKey}|${dr.grade}|${String(dr.contentId)}`;
        const nextRating = toServerRating(ratings[key], dr.rating);
        const nextComment = toServerComment(comments[key], dr.comment);
        const unchanged =
            String(nextRating || "") === String(dr.rating || "") && nextComment === toServerComment(dr.comment);

        return {
            key: dr.key ?? null,
            studentDiffId: dr.studentDiffId,
            gradeToContentId: dr.gradeToContentId,
            gradingProfileStudentId,
            contentId: dr.contentId,
            contentType: dr.contentType,
            rating: nextRating,
            comment: nextComment,
            personalizedLearningPlan: dr.personalizedLearningPlan ?? false,
            lastRaterGuid: unchanged ? (dr.lastRaterGuid ?? raterGuid) : raterGuid
        };
    });

    return { diffRatings };
};

const toServerRating = (gridValue, previous) => {
    if (gridValue === undefined) return previous || "UNRATED";
    if (gridValue === null || gridValue === "") return "UNRATED";
    if (gridValue === "DOES NOT MEET") return "DOES_NOT_MEET";
    return gridValue;
};

export const toServerComment = (gridValue, previous) => {
    const raw = gridValue === undefined ? previous : gridValue;
    return removeHiddenChars(String(raw ?? "").trim()) || null;
};

/**
 * Snapshot as if this student's grid values had just been PUT. Used after a
 * partial save so succeeded students are no longer dirty and failed ones retry.
 */
export const snapshotWithSavedGrid = (snapshot, ratings, comments, studentKey) => ({
    ...snapshot,
    diffRatings: (snapshot.diffRatings || []).map((dr) => {
        const key = `${studentKey}|${dr.grade}|${String(dr.contentId)}`;
        return {
            ...dr,
            rating: toServerRating(ratings[key], dr.rating),
            comment: toServerComment(comments[key], dr.comment)
        };
    })
});

export const studentRatingsChanged = (snapshot, ratings, studentKey, comments = {}) =>
    (snapshot.diffRatings || []).some((dr) => {
        const key = `${studentKey}|${dr.grade}|${String(dr.contentId)}`;
        const gridValue = ratings[key];
        const gridComment = comments[key];
        const ratingChanged =
            gridValue !== undefined && String(toServerRating(gridValue, dr.rating) || "") !== String(dr.rating || "");
        const commentChanged =
            gridComment !== undefined && toServerComment(gridComment) !== toServerComment(dr.comment);
        return ratingChanged || commentChanged;
    });
